import * as React from "react"
import { router } from "@inertiajs/react"
import { team_role_path } from "@/lib/routes"
import { Button } from "@/components/ui/Button"
import { Card } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import { Textarea } from "@/components/ui/Textarea"
import PermissionPicker from "./PermissionPicker"
import RolePeople from "./RolePeople"
import type { Role, Catalog, Member, OrgTreeNode } from "../types"

type Props = {
  role: Role
  catalog: Catalog
  members: Member[]
  orgTree: OrgTreeNode[]
  canAssign: boolean
}

// Right pane of the master-detail layout: always renders the full permission
// catalog; only the checked state changes with the selected role.
export default function RoleDetail({ role, catalog, members, orgTree, canAssign }: Props) {
  const [name, setName] = React.useState(role.name)
  const [description, setDescription] = React.useState(role.description ?? "")
  const [keys, setKeys] = React.useState<string[]>(role.permission_keys)
  const [submitting, setSubmitting] = React.useState(false)

  const locked = role.system || !role.can.update

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.patch(
      team_role_path(role.id),
      { role: { name, description, permission_keys: keys } },
      { preserveState: true, preserveScroll: true, onFinish: () => setSubmitting(false) }
    )
  }

  const destroy = () => {
    if (!confirm("Delete this role?")) return
    router.delete(team_role_path(role.id), { preserveState: true, preserveScroll: true })
  }

  return (
    <Card className="p-6">
      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={locked} required />
              <span className="text-xs text-muted-foreground">slug: {role.slug}</span>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm">Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={locked}
                rows={2}
                placeholder="Plain language — shown to new joiners during onboarding."
              />
            </div>
          </div>

          {/* Add/remove fire their own requests; nothing here is part of the form submit. */}
          <RolePeople role={role} members={members} orgTree={orgTree} canAssign={canAssign} />
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="text-base font-medium text-foreground border-b pb-2">Permissions</h2>
          {role.system && (
            <p className="text-sm text-muted-foreground">
              System role — locked. All permissions granted.
            </p>
          )}
          <PermissionPicker catalog={catalog} selected={keys} onChange={setKeys} disabled={locked} />
        </div>

        <div className="flex items-center justify-between">
          {role.can.destroy ? (
            <Button
              type="button"
              variant="destructive"
              disabled={role.people_count > 0}
              onClick={destroy}
            >
              Delete{role.people_count > 0 ? ` (${role.people_count} active)` : ""}
            </Button>
          ) : (
            <span />
          )}
          {!locked && <Button type="submit" disabled={submitting}>Save</Button>}
        </div>
      </form>
    </Card>
  )
}
